import _ from 'lodash';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useMenuSections from 'src/dispensary/hooks/use-menu-sections';
import { useActiveScheduledItems } from 'src/dispensary/hooks/use-active-scheduled-items';

/**
 * Hook to fetch menu sections for the current dispensary with schedule filtering.
 *
 * Optimized for FCP/CLS: Initially uses menuSections from the dispensary object
 * (available immediately from SSR/cache) to render content without layout shift.
 * Then fetches user-specific sections via useMenuSections which may include
 * personalized content based on user segments. Once user-specific sections load,
 * they replace the initial dispensary sections.
 */
export default function useDispensaryMenuSections() {
  const { dispensary, dispensaryId, loading: dispensaryLoading } = useDispensary();

  const dispensarySuppliedMenuSections = dispensary?.menuSections;

  const { menuSections: directMenuSections, error, ...menuSectionsApolloValues } = useMenuSections({
    dispensaryId: dispensary?.id,
    skip: !dispensary?.id,
  });

  // We're only loading if the dispensary is loading - We want to intially use menuSections from the dispensary data.
  const loading = dispensaryLoading;

  // Once resolved, use the server's order as-is; re-sorting would undo a customized order.
  let resolvedMenuSections = _.sortBy(dispensarySuppliedMenuSections, 'position');
  if (directMenuSections !== undefined && !error) {
    resolvedMenuSections = directMenuSections;
  }

  const menuSections = useActiveScheduledItems({
    items: resolvedMenuSections ?? [],
    getSchedule: (section) => {
      // Only collection sections have schedules
      if (section?.sectionType !== 'COLLECTION') {
        return null;
      }
      return section.collectionSchedule;
    },
    dispensaryTimezone: dispensary?.timezone,
    enablePolling: true,
  });

  return {
    menuSections,
    error,
    dispensaryId,
    ...menuSectionsApolloValues,
    loading, // Must be after spread to override menuSectionsApolloValues.loading
  };
}
